import { forwardRef, useImperativeHandle, useRef, CSSProperties, ReactNode } from 'react';

// ─── Highlight Rules ──────────────────────────────────────────────────────────

interface Rule {
  pattern: RegExp;
  className: string;
}

const RULES: Rule[] = [
  { pattern: /\b(derivative|differentiate|d\/dx|d\/dt)\b/gi, className: 'text-violet-500 font-bold' },
  { pattern: /\b(integrate|integral|antiderivative)\b/gi, className: 'text-teal-500 font-bold' },
  { pattern: /\b(solve|equation|roots|root)\b/gi, className: 'text-emerald-500 font-bold' },
  { pattern: /\b(factor|factored|factoring)\b/gi, className: 'text-cyan-500 font-bold' },
  { pattern: /\b(simplify|simplified|expand|expanded)\b/gi, className: 'text-sky-500 font-bold' },
  { pattern: /\b(limit|lim)\b/gi, className: 'text-amber-500 font-bold' },
  { pattern: /\b(taylor|maclaurin|series)\b/gi, className: 'text-orange-500 font-bold' },
  { pattern: /\b(plot|graph|draw|sketch)\b/gi, className: 'text-rose-500 font-bold' },
  { pattern: /\b(mean|median|mode|variance|std|standard deviation|distribution)\b/gi, className: 'text-indigo-500 font-bold' },
  { pattern: /\b(sin|cos|tan|sinh|cosh|tanh|asin|acos|atan|arcsin|arccos|arctan)\b/gi, className: 'text-pink-500' },
  { pattern: /\b(log|ln|exp|sqrt|abs|floor|ceil|round)\b/gi, className: 'text-fuchsia-500' },
  { pattern: /\b(pi|inf|infinity|euler)\b/gi, className: 'text-amber-400 font-semibold' },
  { pattern: /\b(dx|dy|dt|dz)\b/g, className: 'text-sky-400 italic' },
  { pattern: /\bfrom\b|\bto\b|\bat\b|\bof\b/gi, className: 'text-slate-500 dark:text-slate-400' },
  { pattern: /\b\d+(\.\d+)?\b/g, className: 'text-orange-400 font-semibold' },
];

// ─── Tokeniser ────────────────────────────────────────────────────────────────

interface Token {
  start: number;
  end: number;
  className: string;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];

  for (const { pattern, className } of RULES) {
    const re = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g');
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      const overlaps = tokens.some(
        t => start < t.end && end > t.start
      );
      if (!overlaps) tokens.push({ start, end, className });
    }
  }

  return tokens.sort((a, b) => a.start - b.start);
}

function buildNodes(text: string): ReactNode[] {
  const tokens = tokenize(text);
  const nodes: ReactNode[] = [];
  let pos = 0;

  for (const tok of tokens) {
    if (pos < tok.start) nodes.push(text.slice(pos, tok.start));
    nodes.push(
      <span key={tok.start} className={tok.className}>
        {text.slice(tok.start, tok.end)}
      </span>
    );
    pos = tok.end;
  }
  if (pos < text.length) nodes.push(text.slice(pos));

  return nodes;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface HighlightedTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  /** Extra classes applied to the outer wrapper */
  wrapperClassName?: string;
  isDark?: boolean;
}

const HighlightedTextarea = forwardRef<HTMLTextAreaElement, HighlightedTextareaProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      placeholder = '',
      rows = 5,
      wrapperClassName = '',
      isDark = false,
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => internalRef.current!);

    // Shared layout values
    const sharedStyle: CSSProperties = {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: '14px',
      lineHeight: '1.625',
      padding: '12px 16px',
    };

    const nodes = buildNodes(value);

    return (
      <div
        className={`relative rounded-xl border border-slate-200 dark:border-slate-600 focus-within:ring-2 focus-within:ring-violet-400 focus-within:border-transparent transition-all ${wrapperClassName}`}
      >
        {/* ── Highlight overlay (behind textarea) ── */}
        <div
          aria-hidden="true"
          className={`absolute inset-0 rounded-xl pointer-events-none overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
          style={{
            ...sharedStyle,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            zIndex: 1,
          }}
        >
          {value ? (
            <>{nodes}</>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">{placeholder}</span>
          )}
          {/* Trailing non-breaking space prevents the overlay from collapsing */}
          {'\u00A0'}
        </div>

        {/* ── Actual editable textarea (on top, transparent text) ── */}
        <textarea
          ref={internalRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder=""
          rows={rows}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          className="relative w-full rounded-xl bg-transparent resize-none focus:outline-none text-sm"
          style={{
            ...sharedStyle,
            color: 'transparent',
            caretColor: isDark ? '#e2e8f0' : '#1e293b',
            zIndex: 2,
          }}
        />
      </div>
    );
  }
);

HighlightedTextarea.displayName = 'HighlightedTextarea';
export default HighlightedTextarea;
